import { CONSENT_VERSION } from "../lib/privacyConsent";
import React, { useEffect, useState, useRef } from "react";
import { useCart } from "../context/CartContext";
import { X, ShoppingCart, Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone formatting helper
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  
  let formatted = '+7';
  if (digits.length > 1) {
    formatted += '(' + digits.slice(1, 4);
  }
  if (digits.length > 4) {
    formatted += ')' + digits.slice(4, 7);
  }
  if (digits.length > 7) {
    formatted += '-' + digits.slice(7, 9);
  }
  if (digits.length > 9) {
    formatted += '-' + digits.slice(9, 11);
  }
  return formatted;
};

export const CartPopup = ({ onClose }) => {
  const { cartItems, removeFromCart, discountApplied, setDiscountApplied, clearCart } = useCart();
  const [coupon, setCoupon] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [offerConsent, setOfferConsent] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const dialogRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    dialog?.focus();
    const keydown = event => {
      if (event.key === "Escape" && !isLoading) { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const items = [...dialog.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled),[tabindex="0"]')].filter(el => el.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
    };
    dialog?.addEventListener("keydown", keydown);
    return () => { dialog?.removeEventListener("keydown", keydown); previous?.focus(); };
  }, [onClose, isLoading]);

  // Disable background scroll when popup is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Check if figma-pro-support is in cart (special discount rules)
  const hasFigmaProSupport = cartItems.some(item => item.slug === 'figma-pro-support');

  // Calculate total price with individual discounts
  const calculatePrices = () => {
    let total = 0;
    let discountedTotal = 0;
    
    cartItems.forEach(item => {
      const price = item.price || 0;
      total += price;
      
      if (discountApplied === "0291") {
        discountedTotal += Math.round(price / 100);
      } else if (discountApplied === "SALE") {
        // Figma Pro Support gets only 10% discount, others get 20%
        const discountRate = item.slug === 'figma-pro-support' ? 0.10 : 0.20;
        discountedTotal += Math.round(price * (1 - discountRate));
      } else {
        discountedTotal += price;
      }
    });
    
    return { total, discountedTotal };
  };
  
  // Get discount percentage for display
  const getDiscountText = () => {
    if (discountApplied === "0291") return "99%";
    if (discountApplied === "SALE") return hasFigmaProSupport ? "10-20%" : "20%";
    return "";
  };

  const { total: totalPrice, discountedTotal: finalPrice } = calculatePrices();

  // Email validation on blur
  const handleEmailBlur = () => {
    if (email && !emailRegex.test(email.trim())) {
      setEmailError("Некорректный формат E-mail");
    } else {
      setEmailError("");
    }
  };

  // Phone input handler with formatting
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, '');
    
    // Always start with 7 for Russia
    let normalizedDigits = digits;
    if (digits.length > 0 && digits[0] !== '7') {
      normalizedDigits = '7' + digits;
    }
    
    setPhone(formatPhone(normalizedDigits));
  };

  // Phone validation on blur
  const handlePhoneBlur = () => {
    const digits = phone.replace(/\D/g, '');
    if (phone && digits.length < 11) {
      setPhoneError("Введите полный номер телефона");
    } else {
      setPhoneError("");
    }
  };

  // Coupon validation
  const handleApplyCoupon = () => {
    setErrorMessage("");
    const couponCode = coupon.trim().toUpperCase();
    if (["SALE", "0291"].includes(couponCode)) {
      setDiscountApplied(couponCode);
      setCoupon("");
    } else {
      setErrorMessage("Промокод неверный");
    }
  };

  // Form validation
  const isEmailValid = email.trim() !== "" && !emailError && emailRegex.test(email.trim());
  const isPhoneValid = phone.replace(/\D/g, '').length === 11 && !phoneError;
  const formValid = isEmailValid && isPhoneValid && consent && offerConsent;

  // Checkout handler
  const handleCheckout = async () => {
    if (!formValid) return;
    if (!emailRegex.test(email.trim())) {
      setEmailError("Некорректный формат E-mail");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const body = {
        slugs: cartItems.map(item => item.slug),
        promoCode: ["SALE", "0291"].includes(discountApplied) ? discountApplied : "",
        email: email.trim(),
        phone: phone.replace(/\D/g, ""),
        consent: { personalData: consent, offer: offerConsent, version: CONSENT_VERSION },
      };

      // Цель «Начало оплаты» в Метрике: сколько человек дошли до оплаты.
      // В паре с целью «Успешная оплата» даёт отвал между намерением и платежом.
      if (typeof window.ym === "function") {
        window.ym(107179621, "reachGoal", "checkout_start");
      }

      // Payment API is served by the same site through Nginx on Timeweb.
      const response = await fetch("/api/payments/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error("Не удалось связаться с сервисом оплаты. Обновите страницу и попробуйте ещё раз.");
      }

      if (!response.ok) {
        throw new Error(data.detail || "Ошибка при инициации платежа");
      }

      if (data.paymentUrl) {
        // Clear cart before redirect
        clearCart();
        window.location.href = data.paymentUrl;
      } else {
        throw new Error("Не удалось получить ссылку на оплату");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setErrorMessage(error.message || "Произошла ошибка при оплате");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="cart-popup">
      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="payment-loading">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl">
            <Loader2 className="w-12 h-12 text-teal-600 animate-spin mb-4" />
            <p className="text-gray-900 font-semibold text-lg">Переход к оплате...</p>
            <p className="text-gray-500 text-sm mt-1">Пожалуйста, подождите</p>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={() => !isLoading && onClose()}
        data-testid="cart-backdrop"
      />

      {/* Popup container */}
      <div role="dialog" aria-modal="true" aria-labelledby="cart-title" tabIndex={-1} ref={dialogRef} className="cart-dialog relative z-10 w-full max-w-lg bg-white rounded-2xl max-h-[85vh] min-h-[400px] flex flex-col mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-teal-600" />
            <h2 id="cart-title" className="text-xl font-bold text-gray-900">Корзина</h2>
            {cartItems.length > 0 && (
              <span className="px-2 py-0.5 text-sm font-medium bg-teal-100 text-teal-700 rounded-full">
                {cartItems.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Закрыть корзину" data-testid="cart-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems && cartItems.length > 0 ? (
            <>
              {/* Cart items list */}
              <ul className="space-y-3 mb-6">
                {cartItems.map((item) => (
                  <li
                    key={item.slug}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-gray-100 transition-colors"
                    data-testid={`cart-item-${item.slug}`}
                  >
                    <div className="flex-1 mr-4">
                      <span className="font-medium text-gray-900 text-sm break-words">
                        {item.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900 whitespace-nowrap">
                        {item.price?.toLocaleString('ru-RU')} ₽
                      </span>
                      <button
                        onClick={() => removeFromCart(item.slug)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label={`Удалить ${item.title} из корзины`} data-testid={`remove-item-${item.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Email and phone inputs */}
              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="cart-email" className="block text-sm font-medium mb-2">Электронная почта</label>
                  <input id="cart-email" autoComplete="email" aria-describedby="cart-email-help" aria-invalid={!!emailError}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="Ваш email"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    data-testid="cart-email-input"
                  />
                  <p id="cart-email-help" className="text-sm text-gray-500 mt-2">На эту почту придёт приглашение к курсу в Skillspace.</p>
                  {emailError && (
                    <p className="mt-1 text-sm text-red-500">{emailError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="cart-phone" className="block text-sm font-medium mb-2">Телефон</label>
                  <input id="cart-phone" autoComplete="tel" aria-invalid={!!phoneError}
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder="+7(___) ___-__-__"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    data-testid="cart-phone-input"
                  />
                  {phoneError && (
                    <p className="mt-1 text-sm text-red-500">{phoneError}</p>
                  )}
                </div>
              </div>

              {/* Consent checkboxes */}
              <div className="space-y-3 mb-6">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    data-testid="cart-consent-checkbox"
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                    Я соглашаюсь на обработку{" "}
                    <a
                      href="/personal-data-consent"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 underline"
                    >
                      персональных данных
                    </a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={offerConsent}
                    onChange={(e) => setOfferConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    data-testid="cart-offer-checkbox"
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                    Я соглашаюсь с{" "}
                    <a
                      href="/public-offer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 underline"
                    >
                      публичной офертой
                    </a>
                  </span>
                </label>
              </div>

              {/* Coupon input */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                    aria-label="Промокод" placeholder="Введите промокод"
                    disabled={discountApplied}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                    data-testid="cart-coupon-input"
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={discountApplied || !coupon.trim()}
                    className="px-6 bg-teal-600 hover:bg-teal-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    data-testid="cart-apply-coupon-btn"
                  >
                    Применить
                  </Button>
                </div>
                {discountApplied && (
                  <p className="mt-2 text-sm text-green-600 font-medium">
                    ✓ Скидка {getDiscountText()} применена!
                  </p>
                )}
                {!discountApplied && errorMessage && (
                  <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
                )}
              </div>

              {/* Total price */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mb-4">
                <span className="text-gray-600">Итого:</span>
                <div className="text-right">
                  {discountApplied && (
                    <span className="text-sm text-gray-400 line-through mr-2">
                      {totalPrice.toLocaleString('ru-RU')} ₽
                    </span>
                  )}
                  <span className="text-xl font-bold text-gray-900">
                    {finalPrice.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingCart className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Корзина пуста</p>
              <p className="text-gray-400 text-sm mt-1">Добавьте курсы для покупки</p>
            </div>
          )}
        </div>

        {/* Footer with checkout button */}
        {cartItems.length > 0 && (
          <div className="p-6 border-t border-gray-100 bg-white rounded-b-2xl">
            <Button
              onClick={handleCheckout}
              disabled={!formValid || isLoading}
              className="w-full py-4 text-base font-semibold bg-teal-600 hover:bg-teal-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
              data-testid="cart-checkout-btn"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Обработка...
                </span>
              ) : (
                "Перейти к оплате"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPopup;
